import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OnDestroyHookDirective } from '../common/hooks';
import {
  IOdbCoverDetailDivisionSummaryCategories,
  IOdbBillProductSplit,
  IBillingDocument,
  IOdbBillCategoryDetailsResponse,
  IBillingAccount,
  OdbCoverDetailDivision,
} from '@dch/billing-business-logic';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs/operators';
import { ApplicationState } from 'src/app/state/reducers';
import { selectOdbCategoryDetailsByDocumentReferenceAndCategorySelector } from './../../state/selectors/odb-category-details.selectors';
import { getOdbCategoryDetailsRequest } from './../../state/actions/odb-category-details.actions';
import { SelectedOdbDetailsInfo } from '../models/pii.model';
import {
  getProductIdsFromProductSplit,
  ODB_DETAILS_SESSION_DATA_KEY,
  UtilsService,
} from '../services/utils.service';
import { OVERVIEW_PAGE } from '../config/my-bill.constants';
import {
  selectCustomer,
  selectCustomerAccount,
} from 'src/app/state/selectors/customer-account.selector';
import { InceptionService } from '@dch/ng-common';
import { BillingAnalyticsService } from './../services/billing-analytics.service';
import { getExtraCostConfigFileRequest } from 'src/app/state/actions';
import {
  selectExtraCostConfigFile,
  selectExtraCostInfoPagesByProductIds,
} from 'src/app/state/selectors/extra-cost.selectors';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';

@Component({
  selector: 'my-bill-odb-category-details-page',
  templateUrl: './odb-category-details-page.component.html',
  standalone: false,
})
export class OdbCategoryDetailsPageComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  constructor(
    private router: Router,
    private store: Store<ApplicationState>,
    private utilsService: UtilsService,
    private billingAnalyticsService: BillingAnalyticsService,
    private inceptionService: InceptionService
  ) {
    super();
  }

  readonly selectedCustomer$ = this.store.select(selectCustomer);
  readonly customerAccount$ = this.store.select(selectCustomerAccount);
  readonly extraCostCofigFile$ = this.store.select(selectExtraCostConfigFile);

  selectedOdbDetailsInfo?: SelectedOdbDetailsInfo;
  division?: OdbCoverDetailDivision;
  category?: keyof IOdbCoverDetailDivisionSummaryCategories;
  amount?: string;
  openTransactionToPayIndex?: number;
  documentReference?: string;
  billingAccount?: IBillingAccount;
  hasError: boolean;
  isFetching: boolean;
  productSplit?: Array<IOdbBillProductSplit>;
  filteredProductSplit: Array<IOdbBillProductSplit>;
  categoryLabelKey: string;
  billingDocument: IBillingDocument;
  hasNegativeTotalAmount?: boolean;
  hasDataAvailable: boolean;
  address?: string;
  customerId?: number;
  customerSubsegment?: string;
  customerGlobalId?: string;
  customerMobile?: string;
  selectedProductSplitIdentifier = 'All';
  productIdsFromItems: string[] = [];
  explanationsIntroPages: string[] = [];
  messageBoxTypeError = MessageBoxTypesEnum.Error;

  ngOnInit() {
    this.selectedOdbDetailsInfo =
      this.utilsService.getInfoFromSessionStorage<SelectedOdbDetailsInfo>(
        ODB_DETAILS_SESSION_DATA_KEY
      );
    this.billingAccount = this.selectedOdbDetailsInfo?.billingAccount;
    this.documentReference =
      this.selectedOdbDetailsInfo?.billDocument.reference;

    this.hasDataAvailable =
      this.selectedOdbDetailsInfo !== undefined &&
      this.billingAccount !== undefined &&
      this.documentReference !== undefined;

    if (this.hasDataAvailable) {
      this.extractSelectedOdbDetailsInfo();
      this.getPageImpressionTagging();
      this.setCategoryLabelKey();
      this.hasNegativeTotalAmount = Number(this.amount) < 0;
      this.fetchExtraCostConfigIfNeeded();
      this.dispatchCategoryDetailsRequest();
      this.subscribeToCategoryDetails();
      this.scrollToTop();
    } else {
      this.router.navigate([OVERVIEW_PAGE]);
    }
  }

  private extractSelectedOdbDetailsInfo() {
    this.division = this.selectedOdbDetailsInfo.division;
    this.category = this.selectedOdbDetailsInfo.category;
    this.amount = this.selectedOdbDetailsInfo.amount;
    this.openTransactionToPayIndex =
      this.selectedOdbDetailsInfo.openTransactionToPayIndex;
    this.billingDocument = this.selectedOdbDetailsInfo.billDocument;
    this.address = this.selectedOdbDetailsInfo.address;
  }

  private setCategoryLabelKey() {
    this.categoryLabelKey = this.category
      ? `bills.common.categories.${this.category}`
      : `bills.cover.identifierLabels.${this.division.type}`;
  }

  private fetchExtraCostConfigIfNeeded() {
    if (this.category === 'oneTimeCharge') {
      this.addSubscription(
        this.extraCostCofigFile$
          .pipe(
            filter(
              (config) =>
                (!config.hasError &&
                  config.extraCostConfig === null &&
                  !config.isFetching) ||
                (config.hasError && config.extraCostConfig === null)
            ),
            tap(() => this.store.dispatch(getExtraCostConfigFileRequest()))
          )
          .subscribe()
      );
    }
  }

  private dispatchCategoryDetailsRequest() {
    let divisionIdentifier: string | undefined;
    if (this.category && this.division?.type !== 'other') {
      divisionIdentifier =
        this.division.type === 'address'
          ? this.division.addressNumberIdentifier
          : this.division.identifier;
    }

    this.store.dispatch(
      getOdbCategoryDetailsRequest({
        billingAccountNumber: this.billingAccount.accountNumber,
        documentReference: this.documentReference,
        category: this.category ?? this.division.type,
        divisionType: this.category ? this.division.type : undefined,
        divisionIdentifier,
      })
    );
  }

  private getPageImpressionTagging() {
    this.addSubscription(
      this.customerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.customerId = customerAccount.getSelectedCustomer().customerId;
            this.customerSubsegment =
              customerAccount.getSelectedCustomer().subsegment;
            this.customerGlobalId = customerAccount.globalId;
            this.customerMobile = customerAccount.mobile;

            this.billingAnalyticsService.reportPageImpressionEvent(
              'detail.' + (this.category ?? this.division.type),
              this.documentReference,
              this.billingAccount.accountNumber,
              undefined,
              this.customerId,
              this.customerSubsegment,
              this.customerGlobalId,
              this.customerMobile
            );
          })
        )
        .subscribe()
    );
  }

  private subscribeToCategoryDetails() {
    this.addSubscription(
      this.store
        .select(
          selectOdbCategoryDetailsByDocumentReferenceAndCategorySelector(
            this.documentReference,
            this.category ?? this.division.type
          )
        )
        .pipe(
          filter(
            (odbCategoryDetailsState) => odbCategoryDetailsState !== undefined
          ),
          tap((odbCategoryDetailsState) => {
            this.productSplit = odbCategoryDetailsState.result?.productSplit;
            this.filteredProductSplit = this.productSplit;
            if (this.category === 'oneTimeCharge') {
              this.extractExtraInfoPageKeys(
                getProductIdsFromProductSplit(this.filteredProductSplit)
              );
            }

            this.hasError = odbCategoryDetailsState.hasError;
            this.isFetching = odbCategoryDetailsState.isFetching;
          })
        )
        .subscribe()
    );
  }

  handleChangeOfSelectedItendifier(identifier: string) {
    this.filteredProductSplit =
      identifier === 'All'
        ? this.productSplit
        : this.productSplit.filter(
            (ps: IOdbBillProductSplit) => ps.identifier === identifier
          );
    this.selectedProductSplitIdentifier = identifier;
    if (this.category === 'oneTimeCharge') {
      this.extractExtraInfoPageKeys(
        getProductIdsFromProductSplit(this.filteredProductSplit)
      );
    }
  }

  shouldDisplayInfoSection() {
    return (
      (this.division.type === 'settlement' && Number(this.amount) !== 0) ||
      (Number(this.amount) > 0 &&
        (['usage', 'oneTimeCharge'].includes(this.category) ||
          'thirdParty' === this.division.type ||
          ('subscription' === this.category &&
            this.productSplit &&
            this.shouldDisplaySubscriptionExplanationBtn(this.productSplit))))
    );
  }

  shouldDisplaySubscriptionExplanationBtn = (
    productSplit: IOdbBillCategoryDetailsResponse['productSplit']
  ) =>
    productSplit.some(
      (aProductSplit) =>
        (aProductSplit.categoryDetails?.length ?? 0) > 1 ||
        (aProductSplit.categoryDetails?.some(
          (categoryDetail) => categoryDetail.isProRated
        ) ??
          false)
    );

  private extractExtraInfoPageKeys(productIdsFromItems: string[]) {
    if (productIdsFromItems.length > 0) {
      this.addSubscription(
        this.store
          .select(selectExtraCostInfoPagesByProductIds(productIdsFromItems))
          .pipe(
            tap((infoPages) => {
              this.explanationsIntroPages =
                infoPages.length === 0 ? ['oneTimeCharge'] : infoPages;
            })
          )
          .subscribe()
      );
    } else {
      this.explanationsIntroPages = ['oneTimeCharge'];
    }
  }

  private scrollToTop(): void {
    setTimeout(() => this.inceptionService.scrollToElement('#maincontent'));
  }
}
